<template>
  <div class="worldviews-page">
    <sp-tab active="worldViews" :hidden="!!worldViewId" />
    <list-template v-if="worldViews" :active="!!worldViewId" :selected="selectedWorldView">
      <div slot="side" v-if="worldViewFolders && isShowFolderList">
        <world-view-folder-list
          :novelId="novelId"
          :items="worldViewFolders"
          :worldViewsCount="worldViewsCount"
          :isInsert="isFolderInsertMode"
          :selectedWorldView="targetWorldView"
          @selectedFolder="selectFolder"
          @clickCompleteSetFolder="finishFolderSelect()"
        />
      </div>
      <div slot="side" v-else>
        <world-view-list
          slot="side"
          :novelId="novelId"
          :worldViews="worldViews"
          :selectedId="selectedWorldView && selectedWorldView.worldViewId"
          :folder="worldViewFolder"
          :allFolders="worldViewFolders"
          @backToFolderList="showFolderList()"
          @clickSetFolder="setFolder"
        />
      </div>
      <back-to-list slot="back-to-list" :to="{ name: 'worldViews', params: { novelId } }">{{
        $t("worldViewPage.backToList")
      }}</back-to-list>
      <world-view-detail
        v-if="selectedWorldView"
        slot="main"
        :novelId="novelId"
        :worldView="selectedWorldView"
        :editorMenuVisible="true"
        @showHistoryDialog="isHistoryManageDialogOpen = true"
      />
    </list-template>
    <history-manage-dialog
      v-if="selectedWorldView"
      :is-open="isHistoryManageDialogOpen"
      data-type="wv"
      :title="$t('historyManageDialog.title.worldViewDetail') + ' - ' + (selectedWorldView.name || 'No Name')"
      :novel-id="novelId"
      :data-id="selectedWorldView.worldViewId"
      @close="isHistoryManageDialogOpen = false"
      @restore="onWorldViewRestore"
    >
      <template v-slot:notification> {{ $t('historyManageDialog.notification.localImageExcluded') }} </template>
      <template v-slot:content="{ historyItem }">
        <world-view-detail
          v-if="historyItem"
          :novelId="novelId"
          :worldView="{
            ...historyItem.item,
            worldViewId: selectedWorldView.worldViewId,
          }"
          :editorMenuVisible="false"
        />
      </template>
    </history-manage-dialog>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ListTemplate from "@/components/templates/ListTemplate.vue";
import WorldViewList from "@/components/organisms/WorldViewList.vue";
import WorldViewFolderList from "@/components/organisms/WorldViewFolderList.vue";
import WorldViewDetail from "@/components/organisms/WorldViewDetail.vue";
import HistoryManageDialog from "@/components/ui/dialogs/HistoryManageDialog.vue";
import SpTab from "@/components/molecules/SpTab.vue";
import BackToList from "@/components/atoms/BackToList.vue";
import { Plan, WorldView, WorldViewFolder } from "@/lib/models";
import { cloneDeep } from "lodash";
import {
  hasHistoryQuery,
  omitHistoryQuery,
  shouldRedirectEmptyListToCreate,
} from "@/lib/utils/deepLink";
// NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
// import { Dialog } from "@/lib/utils";
// import PremiumFeatureDialog, { PremiumFeatureDialogProps } from "@/components/ui/PremiumFeatureDialog.vue";
// import { downloadPublicImage } from "@/lib/s3";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    ListTemplate,
    WorldViewList,
    WorldViewFolderList,
    WorldViewDetail,
    HistoryManageDialog,
    BackToList,
    SpTab,
  },
  props: {
    novelId: String,
    worldViewId: String,
  },
  data() {
    return {
      isShowFolderList: false,
      isFolderInsertMode: false,
      targetWorldView: null,
      selectedFolderId: null,
      isHistoryManageDialogOpen: false,
    };
  },
  async created() {
    const { novelId } = this;
    await this.$store.dispatch("worldViewModule/initialize", novelId);
    if (shouldRedirectEmptyListToCreate(this.worldViewId) && !this.worldViews.length) {
      this.$router.push({ name: "worldViewCreate", params: { novelId } });
      return;
    }
    this.openHistoryFromQuery();
  },
  computed: {
    worldViews() {
      const { $store, selectedFolderId } = this;

      let worldViews = $store.getters["worldViewModule/worldViewList"] as WorldView[];

      if (selectedFolderId) {
        worldViews = $store.getters["worldViewModule/worldViewsInFolder"](selectedFolderId) as WorldView[];
      }

      return cloneDeep(worldViews);
    },
    // ストアの更新（固有名詞モーダルでの一括置換など）に追従できるよう、
    // data() の一度きりのスナップショットではなく computed で都度導出する
    selectedWorldView() {
      const list = this.worldViews;
      if (!list.length) return null;
      if (this.worldViewId) {
        return list.find((w) => w.worldViewId === this.worldViewId) || null;
      }
      return list[0];
    },
    worldViewFolder() {
      const { selectedFolderId } = this;
      const worldViewFolder = this.$store.getters["worldViewModule/worldViewFolder"](selectedFolderId);
      return cloneDeep(worldViewFolder);
    },
    worldViewFolders() {
      const worldViewFolders = this.$store.getters["worldViewModule/worldViewFolderList"];
      return cloneDeep(worldViewFolders);
    },
    worldViewsCount() {
      const worldViews = this.$store.getters["worldViewModule/worldViewList"] as WorldView[];
      return worldViews.length;
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFree() {
      return this.plan === Plan.free;
    },
  },
  methods: {
    async selectFolder(worldViewFolderId) {
      this.selectedFolderId = worldViewFolderId;
      this.isShowFolderList = false;
    },
    async showFolderList() {
      if (this.isFree) {
        this.showPremiumFeatureDialog();
        return;
      }

      this.isShowFolderList = true;
    },
    setFolder(el) {
      if (this.isFree) {
        this.showPremiumFeatureDialog();
        return;
      }

      this.targetWorldView = el.worldView;
      this.selectedFolderId = el.worldViewFolder ? el.worldViewFolder.worldViewFolderId : null;
      this.isFolderInsertMode = true;
      this.isShowFolderList = true;
    },
    async finishFolderSelect() {
      await this.selectFolder(this.selectedFolderId);

      this.targetWorldView = null;
      this.isFolderInsertMode = false;
      this.isShowFolderList = false;
    },
    async onWorldViewRestore() {
      this.isHistoryManageDialogOpen = false;
      await this.$store.dispatch("worldViewModule/initialize", this.novelId);
    },
    openHistoryFromQuery() {
      if (!hasHistoryQuery(this.$route.query) || !this.selectedWorldView) return;
      this.isHistoryManageDialogOpen = true;
      this.$router.replace({
        name: this.$route.name as string,
        params: this.$route.params,
        query: omitHistoryQuery(this.$route.query) as Record<string, string | (string | null)[]>,
      });
    },
    async showPremiumFeatureDialog() {
      // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
      // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
      // const data: PremiumFeatureDialogProps = {
      //   title: "フォルダ機能はプレミアム機能です",
      //   text: "Nolaプレミアムにご登録いただくと、お好きな名前で資料を管理できる『フォルダ管理機能』をご利用いただけます。”〇〇国”や”〇〇星”などの世界ごとの管理や、キャラクターが登場するシーンごとの資料管理ができますので、お手すきの際にぜひご活用いただだけたら幸いです。",
      //   image: (await downloadPublicImage("shared/images/gif/worldview_folder.gif")) as string,
      // };
      // premiumFeatureDialog.show(data);

      this.$router.push({ name: "subscriptionAnnounce", query: { from: "worldviewFolder" } });
    },
  },
});

interface Props {
  novelId: string;
  worldViewId: string;
}

interface Data {
  isShowFolderList: boolean;
  isFolderInsertMode: boolean;
  targetWorldView: WorldView | null;
  selectedFolderId: string | null;
  isHistoryManageDialogOpen: boolean;
}

interface Computed {
  worldViews: WorldView[];
  selectedWorldView: WorldView | null;
  worldViewFolder: WorldViewFolder;
  worldViewFolders: WorldViewFolder[];
  worldViewsCount: number;
  plan: Plan;
  isFree: boolean;
}

interface Methods {
  selectFolder(worldViewFolderId: string): Promise<void>;
  showFolderList(): Promise<void>;
  setFolder(el): void;
  finishFolderSelect(): Promise<void>;
  onWorldViewRestore(): Promise<void>;
  showPremiumFeatureDialog(): Promise<void>;
  openHistoryFromQuery(): void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.worldviews-page {
  @include mq(sp) {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-height: 0;
    overflow: hidden;

    // タブの高さを考慮してListTemplateの高さを調整
    .list-template {
      flex: 1;
      min-height: 0;
    }
  }
}

.content-main {
  margin: $margin-m;
}
</style>
